import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface OnboardingState {
  id: string;
  user_id: string;
  has_seen_welcome: boolean;
  has_created_first_lead: boolean;
  has_qualified_first_lead: boolean;
  has_opened_sales_mode: boolean;
  has_created_first_dossier: boolean;
  has_completed_tour: boolean;
  completed_at: string | null;
}

const DEFAULT_STATE: Omit<OnboardingState, 'id' | 'user_id'> = {
  has_seen_welcome: false,
  has_created_first_lead: false,
  has_qualified_first_lead: false,
  has_opened_sales_mode: false,
  has_created_first_dossier: false,
  has_completed_tour: false,
  completed_at: null,
};

export function useOnboarding() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: onboardingState, isLoading } = useQuery({
    queryKey: ['user-onboarding', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('user_onboarding')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data as OnboardingState | null;
    },
    enabled: !!user?.id,
  });

  const initOnboardingMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_onboarding')
        .insert({ user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-onboarding'] });
    },
  });

  const updateOnboardingMutation = useMutation({
    mutationFn: async (updates: Partial<Omit<OnboardingState, 'id' | 'user_id'>>) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Check if all steps are completed to set completed_at
      const allCompleted = 
        (updates.has_seen_welcome ?? onboardingState?.has_seen_welcome ?? false) &&
        (updates.has_created_first_lead ?? onboardingState?.has_created_first_lead ?? false) &&
        (updates.has_qualified_first_lead ?? onboardingState?.has_qualified_first_lead ?? false) &&
        (updates.has_opened_sales_mode ?? onboardingState?.has_opened_sales_mode ?? false) &&
        (updates.has_created_first_dossier ?? onboardingState?.has_created_first_dossier ?? false);

      const { data, error } = await supabase
        .from('user_onboarding')
        .update({
          ...updates,
          completed_at: allCompleted ? new Date().toISOString() : null,
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-onboarding'] });
    },
  });

  // Calculate progress
  const completedSteps = onboardingState ? [
    onboardingState.has_seen_welcome,
    onboardingState.has_created_first_lead,
    onboardingState.has_qualified_first_lead,
    onboardingState.has_opened_sales_mode,
    onboardingState.has_created_first_dossier,
  ].filter(Boolean).length : 0;

  const totalSteps = 5;
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);

  return {
    onboardingState: onboardingState ?? DEFAULT_STATE as OnboardingState,
    isLoading,
    isNewUser: !onboardingState,
    completedSteps,
    totalSteps,
    progressPercent,
    isOnboardingComplete: onboardingState?.completed_at !== null,
    initOnboarding: initOnboardingMutation.mutateAsync,
    updateOnboarding: updateOnboardingMutation.mutateAsync,
    markWelcomeSeen: () => updateOnboardingMutation.mutateAsync({ has_seen_welcome: true }),
    markLeadCreated: () => updateOnboardingMutation.mutateAsync({ has_created_first_lead: true }),
    markLeadQualified: () => updateOnboardingMutation.mutateAsync({ has_qualified_first_lead: true }),
    markSalesModeOpened: () => updateOnboardingMutation.mutateAsync({ has_opened_sales_mode: true }),
    markDossierCreated: () => updateOnboardingMutation.mutateAsync({ has_created_first_dossier: true }),
    markTourCompleted: () => updateOnboardingMutation.mutateAsync({ has_completed_tour: true }),
  };
}
